import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Room, RoomEvent } from 'livekit-client';
import api from '../api';
import { Play, Square, FileText, Bot, Loader2, Plus, Trash2, Variable } from 'lucide-react';

const STORAGE_KEY_PREFIX = 'braincx-test-variables-';

const loadSavedVariables = (agentId) => {
    try {
        const raw = localStorage.getItem(`${STORAGE_KEY_PREFIX}${agentId}`);
        if (raw) {
            const parsed = JSON.parse(raw);
            if (Array.isArray(parsed) && parsed.length > 0) return parsed;
        }
    } catch (_) {}
    return [{ key: '', value: '' }];
};

const persistVariables = (agentId, variables) => {
    try {
        localStorage.setItem(`${STORAGE_KEY_PREFIX}${agentId}`, JSON.stringify(variables));
    } catch (_) {}
};

const variablesToObject = (variables) => {
    const obj = {};
    for (const v of variables) {
        const k = v.key.trim();
        if (k) obj[k] = v.value;
    }
    return obj;
};

const TestAgentInterfaceInline = ({ agent, suppressWebhooks = false }) => {
    const [isConnected, setIsConnected] = useState(false);
    const [isConnecting, setIsConnecting] = useState(false);
    const [captions, setCaptions] = useState([]);
    const [room, setRoom] = useState(null);
    const [sessionId, setSessionId] = useState(null);
    const [error, setError] = useState(null);
    const [variables, setVariables] = useState(() => loadSavedVariables(agent?.id));
    const [showJsonPreview, setShowJsonPreview] = useState(false);
    
    const audioRef = useRef(null);
    const transcriptRef = useRef(null);
    const sessionIdRef = useRef(null);

    useEffect(() => {
        if (agent?.id) {
            setVariables(loadSavedVariables(agent.id));
        }
    }, [agent?.id]);

    const updateVariables = useCallback((updater) => {
        setVariables(prev => {
            const next = typeof updater === 'function' ? updater(prev) : updater;
            if (agent?.id) persistVariables(agent.id, next);
            return next;
        });
    }, [agent?.id]);

    const handleVarChange = useCallback((index, field, value) => {
        updateVariables(prev => prev.map((v, i) => i === index ? { ...v, [field]: value } : v));
    }, [updateVariables]);

    const addVariable = useCallback(() => {
        updateVariables(prev => [...prev, { key: '', value: '' }]);
    }, [updateVariables]);

    const removeVariable = useCallback((index) => {
        updateVariables(prev => {
            const next = prev.filter((_, i) => i !== index);
            return next.length === 0 ? [{ key: '', value: '' }] : next;
        });
    }, [updateVariables]);

    useEffect(() => {
        if (audioRef.current) {
            const configuredVolume = Math.min(Math.max(agent?.voice_volume ?? 1, 0), 1);
            audioRef.current.volume = configuredVolume;
        }
    }, [agent?.voice_volume]);

    // Auto-scroll transcript to bottom when new captions are added
    useEffect(() => {
        if (transcriptRef.current && captions.length > 0) {
            // Use setTimeout to ensure DOM has updated
            setTimeout(() => {
                if (transcriptRef.current) {
                    transcriptRef.current.scrollTop = transcriptRef.current.scrollHeight;
                }
            }, 0);
        }
    }, [captions]);

    // Start test session
    const startTestSession = async () => {
        try {
            setIsConnecting(true);
            setError(null);
            
            const parsedVariables = variablesToObject(variables);
            const hasVariables = Object.keys(parsedVariables).length > 0;
            
            // Generate unique room name for test with environment prefix
            // Use test-agent-local-* for localhost, test-agent-* for production
            const timestamp = Date.now();
            const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
            const roomPrefix = isLocalhost ? 'test-agent-local-' : 'test-agent-';
            const roomName = `${roomPrefix}${agent.id}-${timestamp}`;
            
            
            // Create test session in backend
            const sessionPayload = {
                room_name: roomName,
                agent_id: agent.id,
                channel: 'test'
            };
            
            if (hasVariables) {
                sessionPayload.variables = parsedVariables;
            }

            // "Don't send webhooks" toggle in the agent editor
            if (suppressWebhooks) {
                sessionPayload.suppress_webhooks = true;
            }
            
            const sessionResponse = await api.post('/sessions/test', sessionPayload);
            
            const testSessionId = sessionResponse.data.session_id;
            setSessionId(testSessionId);
            sessionIdRef.current = testSessionId;

            // Get LiveKit token
            const tokenResponse = await api.post('/token', {
                room: roomName,
                identity: 'test-user'
            });
            
            const { url, token } = tokenResponse.data;
            
            // Create and connect to LiveKit room
            const newRoom = new Room({
                adaptiveStream: true,
                dynacast: true,
                disconnectOnPageLeave: false, // Keep call active when user switches browser tabs
                videoCaptureDefaults: {
                    resolution: { width: 1280, height: 720 }
                }
            });
            
            // Set up event listeners
            newRoom.on(RoomEvent.Connected, () => {
                setIsConnected(true);
                setIsConnecting(false);
                addCaption('system', '✅ Connected to test agent');
            });
            
            newRoom.on(RoomEvent.Disconnected, () => {
                setIsConnected(false);
                addCaption('system', '❌ Disconnected from test agent');
            });

            // When agent disconnects (e.g. end node), update UI in case end_call message was missed
            newRoom.on(RoomEvent.ParticipantDisconnected, (participant) => {
                const remoteCount = newRoom.remoteParticipants.size;
                if (remoteCount === 0) {
                    setIsConnected(false);
                    addCaption('system', '📞 Agent left the call');
                }
            });
            
            newRoom.on(RoomEvent.TrackSubscribed, (track, publication, participant) => {
                if (track.kind === 'audio') {
                    // Play remote audio
                    if (audioRef.current) {
                        track.attach(audioRef.current);
                    }
                }
            });
            
            newRoom.on(RoomEvent.DataReceived, async (payload, participant) => {
                try {
                    const decoder = new TextDecoder();
                    const data = JSON.parse(decoder.decode(payload));
                    
                    // Handle agent-initiated end call (e.g. end node)
                    if (data.type === 'end_call') {
                        addCaption('system', '📞 Call ended by agent');
                        setIsConnected(false);
                        setRoom(null);
                        try {
                            await newRoom.disconnect();
                        } catch (_) {}
                        if (sessionIdRef.current) {
                            try {
                                await api.post(`/sessions/${sessionIdRef.current}/end`);
                            } catch (_) {}
                            sessionIdRef.current = null;
                        }
                        setSessionId(null);
                        return;
                    }
                    
                    // Handle transcription data
                    if (data.type === 'transcription') {
                        addCaption(data.speaker || 'agent', data.text);
                    }
                    
                    // Handle conversation items
                    if (data.type === 'conversation_item') {
                        addCaption(data.speaker || 'agent', data.text);
                    }
                } catch (e) {
                    console.error('Error parsing data packet:', e);
                }
            });
            
            // Connect to room
            await newRoom.connect(url, token);
            setRoom(newRoom);
            
            // Enable microphone
            await newRoom.localParticipant.setMicrophoneEnabled(true);
            
        } catch (err) {
            console.error('❌ Error starting test session:', err);
            setError(err.response?.data?.detail || err.message || 'Failed to start test session');
            setIsConnecting(false);
        }
    };

    // Stop test session
    const stopTestSession = async () => {
        try {
            if (room) {
                await room.disconnect();
                setRoom(null);
            }
            
            if (sessionId) {
                // End session in backend
                await api.post(`/sessions/${sessionId}/end`);
            }
            
            setIsConnected(false);
            setSessionId(null);
            sessionIdRef.current = null;

        } catch (err) {
            console.error('❌ Error stopping test session:', err);
        }
    };

    // Add caption to transcript
    const addCaption = (speaker, text) => {
        if (!text || !text.trim()) return;
        
        setCaptions(prev => {
            // Deduplicate
            const lastCaption = prev[prev.length - 1];
            if (lastCaption && lastCaption.speaker === speaker && lastCaption.text === text) {
                return prev;
            }
            
            return [...prev, {
                speaker,
                text,
                timestamp: new Date().toLocaleTimeString()
            }];
        });
    };

    // Auto-scroll transcript to bottom when new captions are added
    useEffect(() => {
        if (transcriptRef.current && captions.length > 0) {
            // Use setTimeout to ensure DOM has updated
            setTimeout(() => {
                if (transcriptRef.current) {
                    transcriptRef.current.scrollTop = transcriptRef.current.scrollHeight;
                }
            }, 0);
        }
    }, [captions]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (room) {
                room.disconnect();
            }
        };
    }, [room]);

    return (
        <div className="flex-1 flex flex-col overflow-hidden">
            <div className="flex flex-1 overflow-hidden">
                {/* Main content area */}
                <div className="flex-1 flex flex-col bg-background">
                    {/* Connection controls */}
                    <div className="p-5 border-b border-border">
                        <div className="flex items-center gap-3">
                            {!isConnected ? (
                                <button
                                    onClick={startTestSession}
                                    disabled={isConnecting}
                                    className={`px-5 py-2.5 rounded-lg text-sm font-medium flex items-center gap-2 transition-all ${
                                        isConnecting
                                            ? 'bg-muted cursor-not-allowed text-muted-foreground'
                                            : 'bg-success hover:bg-success/90 text-success-foreground shadow-elegant'
                                    }`}
                                >
                                    {isConnecting ? (
                                        <>
                                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                            Connecting...
                                        </>
                                    ) : (
                                        <>
                                            <Play className="w-3.5 h-3.5" />
                                            Start Test Session
                                        </>
                                    )}
                                </button>
                            ) : (
                                <button
                                    onClick={stopTestSession}
                                    className="px-5 py-2.5 bg-destructive hover:bg-destructive/90 text-destructive-foreground rounded-lg text-sm font-medium flex items-center gap-2 transition-all shadow-elegant"
                                >
                                    <Square className="w-3.5 h-3.5" />
                                    Stop Test Session
                                </button>
                            )}
                            
                            {isConnected && (
                                <span className="text-xs text-success font-medium flex items-center gap-1.5">
                                    <div className="w-1.5 h-1.5 bg-success rounded-full animate-pulse"></div>
                                    Connected
                                </span>
                            )}
                        </div>
                        
                        {error && (
                            <div className="mt-3 p-2.5 bg-destructive/10 border border-destructive/30 text-destructive rounded-lg text-xs">
                                {error}
                            </div>
                        )}
                    </div>
                    
                    <div className="flex-1 p-5 overflow-hidden min-h-0">
                        <div className="grid gap-6 lg:grid-cols-2 h-full min-h-0">
                            <div className="flex flex-col min-h-0">
                                <div className="flex items-center justify-between flex-shrink-0">
                                    <div className="flex items-center gap-2">
                                        <Variable className="w-3.5 h-3.5 text-primary" />
                                        <span className="text-xs font-semibold text-foreground uppercase tracking-wide">Saved Variables</span>
                                        <span className="text-[10px] text-muted-foreground bg-muted/40 px-1.5 py-0.5 rounded">persisted</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <button
                                            type="button"
                                            onClick={() => setShowJsonPreview(p => !p)}
                                            className="text-[11px] text-muted-foreground hover:text-foreground transition-colors"
                                        >
                                            {showJsonPreview ? 'Hide JSON' : 'Show JSON'}
                                        </button>
                                        <button
                                            type="button"
                                            onClick={addVariable}
                                            className="flex items-center gap-1 text-xs text-primary hover:text-primary/80 transition-colors font-medium"
                                        >
                                            <Plus className="w-3 h-3" />
                                            Add
                                        </button>
                                    </div>
                                </div>

                                <div className="mt-2 space-y-1.5 overflow-y-auto flex-1 min-h-0 pr-1">
                                    {variables.map((v, i) => (
                                        <div key={i} className="flex items-center gap-1.5 group">
                                            <input
                                                type="text"
                                                value={v.key}
                                                onChange={(e) => handleVarChange(i, 'key', e.target.value)}
                                                placeholder="variable_name"
                                                className="flex-1 min-w-0 font-mono text-xs bg-muted/20 border border-border rounded-md px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                                            />
                                            <span className="text-muted-foreground text-xs">=</span>
                                            <input
                                                type="text"
                                                value={v.value}
                                                onChange={(e) => handleVarChange(i, 'value', e.target.value)}
                                                placeholder="value"
                                                className="flex-[2] min-w-0 font-mono text-xs bg-muted/20 border border-border rounded-md px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => removeVariable(i)}
                                                className="p-1 text-muted-foreground hover:text-destructive transition-colors opacity-0 group-hover:opacity-100"
                                                title="Remove variable"
                                            >
                                                <Trash2 className="w-3 h-3" />
                                            </button>
                                        </div>
                                    ))}
                                </div>

                                {showJsonPreview && (
                                    <pre className="mt-2 font-mono text-[11px] bg-muted/30 border border-border rounded-md p-2.5 overflow-auto max-h-32 text-muted-foreground">
                                        {JSON.stringify(variablesToObject(variables), null, 2)}
                                    </pre>
                                )}

                                <p className="mt-1 text-[11px] text-muted-foreground flex-shrink-0">
                                    Variables are auto-saved and persist across sessions. They replace <code className="text-primary/80">{`{{variable_name}}`}</code> placeholders in the prompt.
                                </p>
                            </div>

                            <div className="flex flex-col min-h-0 overflow-hidden">
                                <div className="flex items-center gap-2 mb-4 flex-shrink-0">
                                    <FileText className="w-4 h-4 text-primary" />
                                    <h3 className="text-sm font-semibold text-foreground">Live Transcript</h3>
                                </div>
                                <div
                                    ref={transcriptRef}
                                    className="overflow-y-scroll pr-2 space-y-2.5"
                                    style={{ 
                                        scrollBehavior: 'smooth',
                                        maxHeight: '500px',
                                        height: '100%',
                                        minHeight: '300px'
                                    }}
                                >
                                    {captions.length === 0 ? (
                                        <div className="text-center py-12 rounded-lg border border-dashed border-border bg-muted/10">
                                            <p className="text-muted-foreground text-sm">
                                                No messages yet. Start the test session to begin.
                                            </p>
                                        </div>
                                    ) : (
                                        captions.map((caption, index) => (
                                            <div
                                                key={index}
                                                className={`p-3 rounded-lg border border-primary/50 ${
                                                    caption.speaker === 'user'
                                                        ? 'bg-primary/10'
                                                        : caption.speaker === 'ai' || caption.speaker === 'agent'
                                                        ? 'bg-secondary-cyan/10'
                                                        : 'bg-warning/10'
                                                }`}
                                                style={{
                                                    boxShadow: '0 0 16px hsl(215 90% 58% / 0.4), 0 0 32px hsl(215 90% 58% / 0.2), inset 0 0 8px hsl(215 90% 58% / 0.1)'
                                                }}
                                            >
                                                <div className="flex justify-between items-start gap-3">
                                                    <div className="flex-1 min-w-0">
                                                        <div className="flex items-center gap-1.5 mb-1.5">
                                                            <span className={`text-xs font-medium ${
                                                                caption.speaker === 'user'
                                                                    ? 'text-primary'
                                                                    : caption.speaker === 'ai' || caption.speaker === 'agent'
                                                                    ? 'text-secondary-cyan'
                                                                    : 'text-warning'
                                                            }`}>
                                                                {caption.speaker === 'user' ? 'You' : 
                                                                 caption.speaker === 'ai' || caption.speaker === 'agent' ? 'Agent' : 
                                                                 'System'}
                                                            </span>
                                                        </div>
                                                        <p className="text-sm text-foreground leading-relaxed">{caption.text}</p>
                                                    </div>
                                                    <span className="text-xs text-muted-foreground flex-shrink-0">
                                                        {caption.timestamp}
                                                    </span>
                                                </div>
                                            </div>
                                        ))
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Agent Info Sidebar */}
                <div className="w-72 bg-card border-l border-border p-5 overflow-y-auto">
                    <div className="flex items-center gap-2 mb-4">
                        <Bot className="w-4 h-4 text-primary" />
                        <h3 className="text-sm font-semibold text-foreground">Agent Info</h3>
                    </div>
                    
                    <div className="p-3.5 bg-muted/10 border border-border rounded-lg space-y-3">
                        <div>
                            <span className="text-xs text-muted-foreground font-medium">Model</span>
                            <p className="text-sm text-foreground mt-1 font-mono">{agent.llm_model || 'N/A'}</p>
                        </div>
                        <div>
                            <span className="text-xs text-muted-foreground font-medium">Voice</span>
                            <p className="text-sm text-foreground mt-1 capitalize">{agent.voice_provider || 'cartesia'}</p>
                        </div>
                        <div>
                            <span className="text-xs text-muted-foreground font-medium">LLM Temperature</span>
                            <p className="text-sm text-foreground mt-1">
                                {Number.isFinite(Number(agent.temperature))
                                    ? Number(agent.temperature).toFixed(2)
                                    : '0.70'}
                            </p>
                        </div>
                        {sessionId && (
                            <div>
                                <span className="text-xs text-muted-foreground font-medium">Session</span>
                                <p className="text-xs text-foreground mt-1 font-mono text-muted-foreground">{sessionId.slice(0, 8)}...</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Hidden audio element for agent voice */}
            <audio ref={audioRef} autoPlay playsInline />
        </div>
    );
};

export default TestAgentInterfaceInline;

